/**
 * Cities data for trial request form
 * Ported from turn-v1/src/data/cities.ts
 * 
 * Includes timezone inference for displaying implied timezone
 */

export interface City {
  value: string;
  label: string;
  country: string;
  timezone?: string;
}

// City to IANA timezone mapping
const CITY_TIMEZONES: Record<string, string> = {
  // North America - USA
  'new-york': 'America/New_York',
  'los-angeles': 'America/Los_Angeles',
  'chicago': 'America/Chicago',
  'houston': 'America/Chicago',
  'phoenix': 'America/Phoenix',
  'philadelphia': 'America/New_York',
  'san-antonio': 'America/Chicago',
  'san-diego': 'America/Los_Angeles',
  'dallas': 'America/Chicago',
  'san-jose': 'America/Los_Angeles',
  'miami': 'America/New_York',
  'seattle': 'America/Los_Angeles',
  'boston': 'America/New_York',
  'washington-dc': 'America/New_York',
  'atlanta': 'America/New_York',
  'denver': 'America/Denver',
  'detroit': 'America/Detroit',
  'minneapolis': 'America/Chicago',
  'portland': 'America/Los_Angeles',
  'las-vegas': 'America/Los_Angeles',
  'orlando': 'America/New_York',
  'nashville': 'America/Chicago',
  'austin': 'America/Chicago',
  'charlotte': 'America/New_York',
  'san-francisco': 'America/Los_Angeles',
  'pittsburgh': 'America/New_York',
  'cleveland': 'America/New_York',
  'st-louis': 'America/Chicago',
  'baltimore': 'America/New_York',
  'new-orleans': 'America/Chicago',
  // Canada
  'toronto': 'America/Toronto',
  'montreal': 'America/Montreal',
  'vancouver': 'America/Vancouver',
  'calgary': 'America/Edmonton',
  'edmonton': 'America/Edmonton',
  'ottawa': 'America/Toronto',
  'winnipeg': 'America/Winnipeg',
  'quebec-city': 'America/Montreal',
  'halifax': 'America/Halifax',
  // Mexico
  'mexico-city': 'America/Mexico_City',
  'guadalajara': 'America/Mexico_City',
  'monterrey': 'America/Monterrey',
  'puebla': 'America/Mexico_City',
  'tijuana': 'America/Tijuana',
  'leon': 'America/Mexico_City',
  'juarez': 'America/Ojinaga',
  'merida': 'America/Merida',
  'san-luis-potosi': 'America/Mexico_City',
  'queretaro': 'America/Mexico_City',
  // Europe
  'london': 'Europe/London',
  'manchester': 'Europe/London',
  'birmingham': 'Europe/London',
  'leeds': 'Europe/London',
  'glasgow': 'Europe/London',
  'paris': 'Europe/Paris',
  'lyon': 'Europe/Paris',
  'marseille': 'Europe/Paris',
  'berlin': 'Europe/Berlin',
  'munich': 'Europe/Berlin',
  'frankfurt': 'Europe/Berlin',
  'hamburg': 'Europe/Berlin',
  'rome': 'Europe/Rome',
  'milan': 'Europe/Rome',
  'naples': 'Europe/Rome',
  'madrid': 'Europe/Madrid',
  'barcelona': 'Europe/Madrid',
  'amsterdam': 'Europe/Amsterdam',
  'rotterdam': 'Europe/Amsterdam',
  'brussels': 'Europe/Brussels',
  'vienna': 'Europe/Vienna',
  'zurich': 'Europe/Zurich',
  'geneva': 'Europe/Zurich',
  'stockholm': 'Europe/Stockholm',
  'copenhagen': 'Europe/Copenhagen',
  'oslo': 'Europe/Oslo',
  'helsinki': 'Europe/Helsinki',
  'dublin': 'Europe/Dublin',
  'lisbon': 'Europe/Lisbon',
  'warsaw': 'Europe/Warsaw',
  'prague': 'Europe/Prague',
  'budapest': 'Europe/Budapest',
  'athens': 'Europe/Athens',
  // Asia
  'tokyo': 'Asia/Tokyo',
  'osaka': 'Asia/Tokyo',
  'nagoya': 'Asia/Tokyo',
  'sapporo': 'Asia/Tokyo',
  'fukuoka': 'Asia/Tokyo',
  'seoul': 'Asia/Seoul',
  'busan': 'Asia/Seoul',
  'incheon': 'Asia/Seoul',
  'daegu': 'Asia/Seoul',
  'daejeon': 'Asia/Seoul',
  'beijing': 'Asia/Shanghai',
  'shanghai': 'Asia/Shanghai',
  'guangzhou': 'Asia/Shanghai',
  'shenzhen': 'Asia/Shanghai',
  'hong-kong': 'Asia/Hong_Kong',
  'taipei': 'Asia/Taipei',
  'singapore': 'Asia/Singapore',
  'kuala-lumpur': 'Asia/Kuala_Lumpur',
  'bangkok': 'Asia/Bangkok',
  'jakarta': 'Asia/Jakarta',
  'manila': 'Asia/Manila',
  'ho-chi-minh': 'Asia/Ho_Chi_Minh',
  'hanoi': 'Asia/Ho_Chi_Minh',
  'mumbai': 'Asia/Kolkata',
  'delhi': 'Asia/Kolkata',
  'bangalore': 'Asia/Kolkata',
  'hyderabad': 'Asia/Kolkata',
  'chennai': 'Asia/Kolkata',
  'kolkata': 'Asia/Kolkata',
  'dubai': 'Asia/Dubai',
  'abu-dhabi': 'Asia/Dubai',
  'riyadh': 'Asia/Riyadh',
  'jeddah': 'Asia/Riyadh',
  'doha': 'Asia/Qatar',
  'istanbul': 'Europe/Istanbul',
  'ankara': 'Europe/Istanbul',
  'tel-aviv': 'Asia/Jerusalem',
  'jerusalem': 'Asia/Jerusalem',
  // Australia & Oceania
  'sydney': 'Australia/Sydney',
  'melbourne': 'Australia/Melbourne',
  'brisbane': 'Australia/Brisbane',
  'perth': 'Australia/Perth',
  'adelaide': 'Australia/Adelaide',
  'auckland': 'Pacific/Auckland',
  'wellington': 'Pacific/Auckland',
  // South America
  'sao-paulo': 'America/Sao_Paulo',
  'rio-de-janeiro': 'America/Sao_Paulo',
  'brasilia': 'America/Sao_Paulo',
  'salvador': 'America/Bahia',
  'fortaleza': 'America/Fortaleza',
  'buenos-aires': 'America/Argentina/Buenos_Aires',
  'cordoba': 'America/Argentina/Cordoba',
  'santiago': 'America/Santiago',
  'lima': 'America/Lima',
  'bogota': 'America/Bogota',
  'medellin': 'America/Bogota',
  'caracas': 'America/Caracas',
  'quito': 'America/Guayaquil',
  'guayaquil': 'America/Guayaquil',
  // Africa
  'cairo': 'Africa/Cairo',
  'alexandria': 'Africa/Cairo',
  'lagos': 'Africa/Lagos',
  'abuja': 'Africa/Lagos',
  'nairobi': 'Africa/Nairobi',
  'mombasa': 'Africa/Nairobi',
  'johannesburg': 'Africa/Johannesburg',
  'cape-town': 'Africa/Johannesburg',
  'durban': 'Africa/Johannesburg',
  'casablanca': 'Africa/Casablanca',
  'rabat': 'Africa/Casablanca',
  'tunis': 'Africa/Tunis',
  'algiers': 'Africa/Algiers',
  'accra': 'Africa/Accra',
  'dakar': 'Africa/Dakar',
};

// Timezone display names
const TIMEZONE_DISPLAY: Record<string, string> = {
  'America/New_York': 'Eastern Time (ET)',
  'America/Chicago': 'Central Time (CT)',
  'America/Denver': 'Mountain Time (MT)',
  'America/Los_Angeles': 'Pacific Time (PT)',
  'America/Phoenix': 'Arizona Time (MST)',
  'America/Detroit': 'Eastern Time (ET)',
  'America/Toronto': 'Eastern Time (ET)',
  'America/Montreal': 'Eastern Time (ET)',
  'America/Vancouver': 'Pacific Time (PT)',
  'America/Edmonton': 'Mountain Time (MT)',
  'America/Winnipeg': 'Central Time (CT)',
  'America/Halifax': 'Atlantic Time (AT)',
  'America/Mexico_City': 'Central Time (CST)',
  'America/Monterrey': 'Central Time (CST)',
  'America/Tijuana': 'Pacific Time (PST)',
  'America/Ojinaga': 'Mountain Time (MST)',
  'America/Merida': 'Central Time (CST)',
  'Europe/London': 'GMT/BST',
  'Europe/Paris': 'Central European Time (CET)',
  'Europe/Berlin': 'Central European Time (CET)',
  'Europe/Rome': 'Central European Time (CET)',
  'Europe/Madrid': 'Central European Time (CET)',
  'Europe/Amsterdam': 'Central European Time (CET)',
  'Europe/Brussels': 'Central European Time (CET)',
  'Europe/Vienna': 'Central European Time (CET)',
  'Europe/Zurich': 'Central European Time (CET)',
  'Europe/Stockholm': 'Central European Time (CET)',
  'Europe/Copenhagen': 'Central European Time (CET)',
  'Europe/Oslo': 'Central European Time (CET)',
  'Europe/Helsinki': 'Eastern European Time (EET)',
  'Europe/Dublin': 'GMT/IST',
  'Europe/Lisbon': 'Western European Time (WET)',
  'Europe/Warsaw': 'Central European Time (CET)',
  'Europe/Prague': 'Central European Time (CET)',
  'Europe/Budapest': 'Central European Time (CET)',
  'Europe/Athens': 'Eastern European Time (EET)',
  'Europe/Istanbul': 'Turkey Time (TRT)',
  'Asia/Tokyo': 'Japan Standard Time (JST)',
  'Asia/Seoul': 'Korea Standard Time (KST)',
  'Asia/Shanghai': 'China Standard Time (CST)',
  'Asia/Hong_Kong': 'Hong Kong Time (HKT)',
  'Asia/Taipei': 'Taiwan Standard Time (TST)',
  'Asia/Singapore': 'Singapore Time (SGT)',
  'Asia/Kuala_Lumpur': 'Malaysia Time (MYT)',
  'Asia/Bangkok': 'Indochina Time (ICT)',
  'Asia/Jakarta': 'Western Indonesia Time (WIB)',
  'Asia/Manila': 'Philippine Time (PHT)',
  'Asia/Ho_Chi_Minh': 'Indochina Time (ICT)',
  'Asia/Kolkata': 'India Standard Time (IST)',
  'Asia/Dubai': 'Gulf Standard Time (GST)',
  'Asia/Riyadh': 'Arabia Standard Time (AST)',
  'Asia/Qatar': 'Arabia Standard Time (AST)',
  'Asia/Jerusalem': 'Israel Standard Time (IST)',
  'Australia/Sydney': 'Australian Eastern Time (AET)',
  'Australia/Melbourne': 'Australian Eastern Time (AET)',
  'Australia/Brisbane': 'Australian Eastern Time (AET)',
  'Australia/Perth': 'Australian Western Time (AWT)',
  'Australia/Adelaide': 'Australian Central Time (ACT)',
  'Pacific/Auckland': 'New Zealand Time (NZST)',
  'America/Sao_Paulo': 'Brasília Time (BRT)',
  'America/Bahia': 'Brasília Time (BRT)',
  'America/Fortaleza': 'Brasília Time (BRT)',
  'America/Argentina/Buenos_Aires': 'Argentina Time (ART)',
  'America/Argentina/Cordoba': 'Argentina Time (ART)',
  'America/Santiago': 'Chile Time (CLT)',
  'America/Lima': 'Peru Time (PET)',
  'America/Bogota': 'Colombia Time (COT)',
  'America/Caracas': 'Venezuela Time (VET)',
  'America/Guayaquil': 'Ecuador Time (ECT)',
  'Africa/Cairo': 'Eastern European Time (EET)',
  'Africa/Lagos': 'West Africa Time (WAT)',
  'Africa/Nairobi': 'East Africa Time (EAT)',
  'Africa/Johannesburg': 'South Africa Time (SAST)',
  'Africa/Casablanca': 'Western European Time (WET)',
  'Africa/Tunis': 'Central European Time (CET)',
  'Africa/Algiers': 'Central European Time (CET)',
  'Africa/Accra': 'Greenwich Mean Time (GMT)',
  'Africa/Dakar': 'Greenwich Mean Time (GMT)',
};

export const cities: City[] = [
  // North America
  { value: 'new-york', label: 'New York', country: 'USA' },
  { value: 'los-angeles', label: 'Los Angeles', country: 'USA' },
  { value: 'chicago', label: 'Chicago', country: 'USA' },
  { value: 'houston', label: 'Houston', country: 'USA' },
  { value: 'phoenix', label: 'Phoenix', country: 'USA' },
  { value: 'philadelphia', label: 'Philadelphia', country: 'USA' },
  { value: 'san-antonio', label: 'San Antonio', country: 'USA' },
  { value: 'san-diego', label: 'San Diego', country: 'USA' },
  { value: 'dallas', label: 'Dallas', country: 'USA' },
  { value: 'san-jose', label: 'San Jose', country: 'USA' },
  { value: 'miami', label: 'Miami', country: 'USA' },
  { value: 'seattle', label: 'Seattle', country: 'USA' },
  { value: 'boston', label: 'Boston', country: 'USA' },
  { value: 'washington-dc', label: 'Washington DC', country: 'USA' },
  { value: 'atlanta', label: 'Atlanta', country: 'USA' },
  { value: 'denver', label: 'Denver', country: 'USA' },
  { value: 'detroit', label: 'Detroit', country: 'USA' },
  { value: 'minneapolis', label: 'Minneapolis', country: 'USA' },
  { value: 'portland', label: 'Portland', country: 'USA' },
  { value: 'las-vegas', label: 'Las Vegas', country: 'USA' },
  { value: 'orlando', label: 'Orlando', country: 'USA' },
  { value: 'nashville', label: 'Nashville', country: 'USA' },
  { value: 'austin', label: 'Austin', country: 'USA' },
  { value: 'charlotte', label: 'Charlotte', country: 'USA' },
  { value: 'san-francisco', label: 'San Francisco', country: 'USA' },
  { value: 'pittsburgh', label: 'Pittsburgh', country: 'USA' },
  { value: 'cleveland', label: 'Cleveland', country: 'USA' },
  { value: 'st-louis', label: 'St. Louis', country: 'USA' },
  { value: 'baltimore', label: 'Baltimore', country: 'USA' },
  { value: 'new-orleans', label: 'New Orleans', country: 'USA' },
  { value: 'toronto', label: 'Toronto', country: 'Canada' },
  { value: 'montreal', label: 'Montreal', country: 'Canada' },
  { value: 'vancouver', label: 'Vancouver', country: 'Canada' },
  { value: 'calgary', label: 'Calgary', country: 'Canada' },
  { value: 'edmonton', label: 'Edmonton', country: 'Canada' },
  { value: 'ottawa', label: 'Ottawa', country: 'Canada' },
  { value: 'winnipeg', label: 'Winnipeg', country: 'Canada' },
  { value: 'quebec-city', label: 'Quebec City', country: 'Canada' },
  { value: 'halifax', label: 'Halifax', country: 'Canada' },
  { value: 'mexico-city', label: 'Mexico City', country: 'Mexico' },
  { value: 'guadalajara', label: 'Guadalajara', country: 'Mexico' },
  { value: 'monterrey', label: 'Monterrey', country: 'Mexico' },
  { value: 'puebla', label: 'Puebla', country: 'Mexico' },
  { value: 'tijuana', label: 'Tijuana', country: 'Mexico' },
  { value: 'leon', label: 'León', country: 'Mexico' },
  { value: 'juarez', label: 'Juárez', country: 'Mexico' },
  { value: 'merida', label: 'Mérida', country: 'Mexico' },
  { value: 'san-luis-potosi', label: 'San Luis Potosí', country: 'Mexico' },
  { value: 'queretaro', label: 'Querétaro', country: 'Mexico' },

  // Europe
  { value: 'london', label: 'London', country: 'UK' },
  { value: 'manchester', label: 'Manchester', country: 'UK' },
  { value: 'birmingham', label: 'Birmingham', country: 'UK' },
  { value: 'leeds', label: 'Leeds', country: 'UK' },
  { value: 'glasgow', label: 'Glasgow', country: 'UK' },
  { value: 'paris', label: 'Paris', country: 'France' },
  { value: 'lyon', label: 'Lyon', country: 'France' },
  { value: 'marseille', label: 'Marseille', country: 'France' },
  { value: 'berlin', label: 'Berlin', country: 'Germany' },
  { value: 'munich', label: 'Munich', country: 'Germany' },
  { value: 'frankfurt', label: 'Frankfurt', country: 'Germany' },
  { value: 'hamburg', label: 'Hamburg', country: 'Germany' },
  { value: 'rome', label: 'Rome', country: 'Italy' },
  { value: 'milan', label: 'Milan', country: 'Italy' },
  { value: 'naples', label: 'Naples', country: 'Italy' },
  { value: 'madrid', label: 'Madrid', country: 'Spain' },
  { value: 'barcelona', label: 'Barcelona', country: 'Spain' },
  { value: 'amsterdam', label: 'Amsterdam', country: 'Netherlands' },
  { value: 'rotterdam', label: 'Rotterdam', country: 'Netherlands' },
  { value: 'brussels', label: 'Brussels', country: 'Belgium' },
  { value: 'vienna', label: 'Vienna', country: 'Austria' },
  { value: 'zurich', label: 'Zurich', country: 'Switzerland' },
  { value: 'geneva', label: 'Geneva', country: 'Switzerland' },
  { value: 'stockholm', label: 'Stockholm', country: 'Sweden' },
  { value: 'copenhagen', label: 'Copenhagen', country: 'Denmark' },
  { value: 'oslo', label: 'Oslo', country: 'Norway' },
  { value: 'helsinki', label: 'Helsinki', country: 'Finland' },
  { value: 'dublin', label: 'Dublin', country: 'Ireland' },
  { value: 'lisbon', label: 'Lisbon', country: 'Portugal' },
  { value: 'warsaw', label: 'Warsaw', country: 'Poland' },
  { value: 'prague', label: 'Prague', country: 'Czech Republic' },
  { value: 'budapest', label: 'Budapest', country: 'Hungary' },
  { value: 'athens', label: 'Athens', country: 'Greece' },

  // Asia
  { value: 'tokyo', label: 'Tokyo', country: 'Japan' },
  { value: 'osaka', label: 'Osaka', country: 'Japan' },
  { value: 'nagoya', label: 'Nagoya', country: 'Japan' },
  { value: 'sapporo', label: 'Sapporo', country: 'Japan' },
  { value: 'fukuoka', label: 'Fukuoka', country: 'Japan' },
  { value: 'seoul', label: 'Seoul', country: 'South Korea' },
  { value: 'busan', label: 'Busan', country: 'South Korea' },
  { value: 'incheon', label: 'Incheon', country: 'South Korea' },
  { value: 'daegu', label: 'Daegu', country: 'South Korea' },
  { value: 'daejeon', label: 'Daejeon', country: 'South Korea' },
  { value: 'beijing', label: 'Beijing', country: 'China' },
  { value: 'shanghai', label: 'Shanghai', country: 'China' },
  { value: 'guangzhou', label: 'Guangzhou', country: 'China' },
  { value: 'shenzhen', label: 'Shenzhen', country: 'China' },
  { value: 'hong-kong', label: 'Hong Kong', country: 'China' },
  { value: 'taipei', label: 'Taipei', country: 'Taiwan' },
  { value: 'singapore', label: 'Singapore', country: 'Singapore' },
  { value: 'kuala-lumpur', label: 'Kuala Lumpur', country: 'Malaysia' },
  { value: 'bangkok', label: 'Bangkok', country: 'Thailand' },
  { value: 'jakarta', label: 'Jakarta', country: 'Indonesia' },
  { value: 'manila', label: 'Manila', country: 'Philippines' },
  { value: 'ho-chi-minh', label: 'Ho Chi Minh City', country: 'Vietnam' },
  { value: 'hanoi', label: 'Hanoi', country: 'Vietnam' },
  { value: 'mumbai', label: 'Mumbai', country: 'India' },
  { value: 'delhi', label: 'Delhi', country: 'India' },
  { value: 'bangalore', label: 'Bangalore', country: 'India' },
  { value: 'hyderabad', label: 'Hyderabad', country: 'India' },
  { value: 'chennai', label: 'Chennai', country: 'India' },
  { value: 'kolkata', label: 'Kolkata', country: 'India' },
  { value: 'dubai', label: 'Dubai', country: 'UAE' },
  { value: 'abu-dhabi', label: 'Abu Dhabi', country: 'UAE' },
  { value: 'riyadh', label: 'Riyadh', country: 'Saudi Arabia' },
  { value: 'jeddah', label: 'Jeddah', country: 'Saudi Arabia' },
  { value: 'doha', label: 'Doha', country: 'Qatar' },
  { value: 'istanbul', label: 'Istanbul', country: 'Turkey' },
  { value: 'ankara', label: 'Ankara', country: 'Turkey' },
  { value: 'tel-aviv', label: 'Tel Aviv', country: 'Israel' },
  { value: 'jerusalem', label: 'Jerusalem', country: 'Israel' },

  // Australia & Oceania
  { value: 'sydney', label: 'Sydney', country: 'Australia' },
  { value: 'melbourne', label: 'Melbourne', country: 'Australia' },
  { value: 'brisbane', label: 'Brisbane', country: 'Australia' },
  { value: 'perth', label: 'Perth', country: 'Australia' },
  { value: 'adelaide', label: 'Adelaide', country: 'Australia' },
  { value: 'auckland', label: 'Auckland', country: 'New Zealand' },
  { value: 'wellington', label: 'Wellington', country: 'New Zealand' },

  // South America
  { value: 'sao-paulo', label: 'São Paulo', country: 'Brazil' },
  { value: 'rio-de-janeiro', label: 'Rio de Janeiro', country: 'Brazil' },
  { value: 'brasilia', label: 'Brasília', country: 'Brazil' },
  { value: 'salvador', label: 'Salvador', country: 'Brazil' },
  { value: 'fortaleza', label: 'Fortaleza', country: 'Brazil' },
  { value: 'buenos-aires', label: 'Buenos Aires', country: 'Argentina' },
  { value: 'cordoba', label: 'Córdoba', country: 'Argentina' },
  { value: 'santiago', label: 'Santiago', country: 'Chile' },
  { value: 'lima', label: 'Lima', country: 'Peru' },
  { value: 'bogota', label: 'Bogotá', country: 'Colombia' },
  { value: 'medellin', label: 'Medellín', country: 'Colombia' },
  { value: 'caracas', label: 'Caracas', country: 'Venezuela' },
  { value: 'quito', label: 'Quito', country: 'Ecuador' },
  { value: 'guayaquil', label: 'Guayaquil', country: 'Ecuador' },

  // Africa
  { value: 'cairo', label: 'Cairo', country: 'Egypt' },
  { value: 'alexandria', label: 'Alexandria', country: 'Egypt' },
  { value: 'lagos', label: 'Lagos', country: 'Nigeria' },
  { value: 'abuja', label: 'Abuja', country: 'Nigeria' },
  { value: 'nairobi', label: 'Nairobi', country: 'Kenya' },
  { value: 'mombasa', label: 'Mombasa', country: 'Kenya' },
  { value: 'johannesburg', label: 'Johannesburg', country: 'South Africa' },
  { value: 'cape-town', label: 'Cape Town', country: 'South Africa' },
  { value: 'durban', label: 'Durban', country: 'South Africa' },
  { value: 'casablanca', label: 'Casablanca', country: 'Morocco' },
  { value: 'rabat', label: 'Rabat', country: 'Morocco' },
  { value: 'tunis', label: 'Tunis', country: 'Tunisia' },
  { value: 'algiers', label: 'Algiers', country: 'Algeria' },
  { value: 'accra', label: 'Accra', country: 'Ghana' },
  { value: 'dakar', label: 'Dakar', country: 'Senegal' },
].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Get timezone for a city by its value
 */
export function getCityTimezone(cityValue: string): string | null {
  return CITY_TIMEZONES[cityValue] || null;
}

/**
 * Get display-friendly timezone string
 */
export function getTimezoneDisplay(timezone: string): string {
  return TIMEZONE_DISPLAY[timezone] || timezone;
}

/**
 * Get city with timezone display string
 */
export function getCityWithTimezone(cityValue: string): string {
  const city = cities.find(c => c.value === cityValue);
  if (!city) return '';
  
  const timezone = getCityTimezone(cityValue);
  if (!timezone) return city.label;
  
  const display = getTimezoneDisplay(timezone);
  return `${city.label} (${display})`;
}

/**
 * Search cities by label (case-insensitive)
 */
export function searchCities(query: string): City[] {
  const lowerQuery = query.toLowerCase();
  return cities.filter(c => c.label.toLowerCase().includes(lowerQuery));
}

/**
 * Get cities by country
 */
export function getCitiesByCountry(country: string): City[] {
  return cities.filter(c => c.country === country);
}

/**
 * Detect timezone from the browser's Intl API.
 * Returns an IANA timezone string (e.g. "America/New_York").
 */
export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York';
  }
}

/**
 * Resolve timezone for a city value.
 * If the city is a known slug from our list, use the mapped timezone.
 * Otherwise, fall back to browser-detected timezone.
 */
export function resolveTimezone(cityValue: string): string {
  return CITY_TIMEZONES[cityValue] || detectBrowserTimezone();
}

/**
 * Get display-friendly timezone string for any IANA timezone,
 * using our display map with a formatted fallback for unknown zones.
 */
export function resolveTimezoneDisplay(timezone: string): string {
  if (TIMEZONE_DISPLAY[timezone]) return TIMEZONE_DISPLAY[timezone];
  try {
    const offset = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'longOffset' })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName')?.value || '';
    const short = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    return offset ? `${short} (${offset})` : short;
  } catch {
    return timezone;
  }
}

/**
 * Find a known city by label (case-insensitive exact match).
 * Returns the city slug or null if not a known city.
 */
export function findCityByLabel(label: string): City | null {
  const lower = label.toLowerCase().trim();
  return cities.find(c => c.label.toLowerCase() === lower) || null;
}

/**
 * Country-to-E.164 calling code mapping.
 * Used to auto-default phone field country code when a city is selected.
 */
const COUNTRY_CALLING_CODES: Record<string, string> = {
  'USA': '1', 'Canada': '1', 'Mexico': '52',
  'UK': '44', 'France': '33', 'Germany': '49', 'Italy': '39', 'Spain': '34',
  'Netherlands': '31', 'Belgium': '32', 'Austria': '43', 'Switzerland': '41',
  'Sweden': '46', 'Denmark': '45', 'Norway': '47', 'Finland': '358',
  'Ireland': '353', 'Portugal': '351', 'Poland': '48',
  'Czech Republic': '420', 'Hungary': '36', 'Greece': '30',
  'Japan': '81', 'South Korea': '82', 'China': '86', 'Taiwan': '886',
  'Singapore': '65', 'Malaysia': '60', 'Thailand': '66', 'Indonesia': '62',
  'Philippines': '63', 'Vietnam': '84', 'India': '91',
  'UAE': '971', 'Saudi Arabia': '966', 'Qatar': '974', 'Turkey': '90', 'Israel': '972',
  'Australia': '61', 'New Zealand': '64',
  'Brazil': '55', 'Argentina': '54', 'Chile': '56', 'Peru': '51',
  'Colombia': '57', 'Venezuela': '58', 'Ecuador': '593',
  'Egypt': '20', 'Nigeria': '234', 'Kenya': '254', 'South Africa': '27',
  'Morocco': '212', 'Tunisia': '216', 'Algeria': '213', 'Ghana': '233', 'Senegal': '221',
};

/**
 * Get the E.164 calling code for a city's country.
 * Returns null if the country/city is unknown.
 */
export function getCallingCodeForCity(cityValue: string): string | null {
  const city = cities.find(c => c.value === cityValue);
  if (!city) return null;
  return COUNTRY_CALLING_CODES[city.country] || null;
}

/**
 * Get the country name for a city value.
 */
export function getCityCountry(cityValue: string): string | null {
  const city = cities.find(c => c.value === cityValue);
  return city?.country || null;
}

const PHONE_FORMAT_EXAMPLES: Record<string, string> = {
  '1': '+1 (555) 123-4567',
  '44': '+44 7911 123456',
  '49': '+49 151 12345678',
  '33': '+33 6 12 34 56 78',
  '39': '+39 312 345 6789',
  '34': '+34 612 345 678',
  '31': '+31 6 12345678',
  '32': '+32 470 12 34 56',
  '43': '+43 664 1234567',
  '41': '+41 78 123 45 67',
  '46': '+46 70 123 45 67',
  '45': '+45 20 12 34 56',
  '47': '+47 412 34 567',
  '358': '+358 40 1234567',
  '353': '+353 85 123 4567',
  '351': '+351 912 345 678',
  '48': '+48 512 345 678',
  '420': '+420 601 234 567',
  '36': '+36 20 123 4567',
  '30': '+30 691 234 5678',
  '52': '+52 55 1234 5678',
  '81': '+81 90 1234 5678',
  '82': '+82 10 1234 5678',
  '86': '+86 131 1234 5678',
  '886': '+886 912 345 678',
  '65': '+65 9123 4567',
  '60': '+60 12 345 6789',
  '66': '+66 81 234 5678',
  '62': '+62 812 345 6789',
  '63': '+63 917 123 4567',
  '84': '+84 91 234 56 78',
  '91': '+91 98765 43210',
  '971': '+971 50 123 4567',
  '966': '+966 50 123 4567',
  '974': '+974 5512 3456',
  '90': '+90 532 123 4567',
  '972': '+972 50 123 4567',
  '61': '+61 412 345 678',
  '64': '+64 21 123 4567',
  '55': '+55 11 91234 5678',
  '54': '+54 11 1234 5678',
  '56': '+56 9 1234 5678',
  '51': '+51 912 345 678',
  '57': '+57 310 123 4567',
  '58': '+58 412 123 4567',
  '593': '+593 99 123 4567',
  '20': '+20 10 1234 5678',
  '234': '+234 801 234 5678',
  '254': '+254 712 345678',
  '27': '+27 71 123 4567',
  '212': '+212 6 12 34 56 78',
  '216': '+216 20 123 456',
  '213': '+213 5 12 34 56 78',
  '233': '+233 24 123 4567',
  '221': '+221 77 123 45 67',
};

/**
 * Get an example phone format for a calling code.
 * Falls back to generic "+{code} (your number)" if no format is defined.
 */
export function getPhoneFormatExample(callingCode: string): string {
  return PHONE_FORMAT_EXAMPLES[callingCode] || `+${callingCode} (your number)`;
}
